import React, { useState } from 'react'
import { Calendar } from 'react-date-range'
import moment from 'moment'
import classNames from 'classnames'
import { addDays } from 'date-fns'

import { Button } from '@/components/buttons/Button'
import { InteractiveTooltip } from '@/components/tooltips/InteractiveTooltip'

import './DateRangePicker.scss'

/**
 * Single-date counterpart to DateRangePickerReusable, sharing its trigger/popover
 * styling. Emits the picked native Date via onChange.
 *
 * @import import { SingleDatePickerReusable } from '@/components/datePickers/SingleDatePickerReusable'
 * @param {object} props
 * @param {Date|string|null} [props.date] Currently selected date
 * @param {(date: Date) => void} props.onChange Called with the picked native Date
 * @param {boolean} [props.allowFutureDates] Allows picking dates after today
 * @param {boolean} [props.allowPastDates] Allows picking dates before today
 * @param {boolean} [props.error] Toggles the error class on the trigger; no critical styles are currently defined for it
 * @param {string} [props.placeholder] Trigger text while no date is selected
 */
export const SingleDatePickerReusable = ({
  date = null,
  onChange,
  allowFutureDates = true,
  allowPastDates = false,
  error = false,
  placeholder = 'Select Date',
  ...props
}) => {
  const [open, setOpen] = useState(false)
  const now = moment().toDate()
  const maxDate = allowFutureDates ? addDays(new Date(), 10000) : now
  const minDate = allowPastDates ? addDays(new Date(), -10000) : now
  const selectedDate = date ? moment(date).toDate() : null
  const label = selectedDate ? moment(selectedDate).format('L') : placeholder

  const handleChange = (nextDate) => {
    onChange(nextDate)
    setOpen(false)
  }

  return (
    <InteractiveTooltip
      block
      clickable
      open={open}
      setOpen={setOpen}
      placement="bottom-start"
      theme="light"
      className="date-range-picker-popup"
      content={
        <Calendar
          date={selectedDate || now}
          onChange={handleChange}
          minDate={minDate}
          maxDate={maxDate}
          color="var(--color-brand-control)"
        />
      }
    >
      <Button
        fullWidth
        className={classNames('date-picker-trigger', { error, 'form-active': open })}
        data-testid={props['data-testid']}
      >
        {label}
      </Button>
    </InteractiveTooltip>
  )
}
